/** 志愿者轨迹渲染：语义色、时间渐变与方向箭头数据构建（供 L7 图层消费） */

export type TrackPoint = { lat: number; lon: number; recordedAt: number }

export type TrackTask = {
  taskId: string
  status: string
  track: TrackPoint[]
}

export type TrackRenderPoint = { lat: number; lon: number; t: number; color: string }
export type TrackRenderArrow = { lat: number; lon: number; heading: number; color: string }

/** 语义色：进行中 / 已完成 / 已超时 */
export const TRACK_COLOR_ACTIVE = '#56d7ff'
export const TRACK_COLOR_DONE = '#8bdbb5'
export const TRACK_COLOR_TIMEOUT = '#8b98ab'

export function taskTrackColor(status: string): string {
  if (status === 'done') return TRACK_COLOR_DONE
  if (status === 'timeout' || status === 'rejected') return TRACK_COLOR_TIMEOUT
  return TRACK_COLOR_ACTIVE
}

function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '')
  return [
    parseInt(value.slice(0, 2), 16),
    parseInt(value.slice(2, 4), 16),
    parseInt(value.slice(4, 6), 16),
  ]
}

/** 线性插值混合两个 #rrggbb 颜色 */
export function mixHex(a: string, b: string, t: number): string {
  const ca = hexToRgb(a)
  const cb = hexToRgb(b)
  const mix = ca.map((v, i) => Math.round(v + (cb[i] - v) * Math.min(1, Math.max(0, t))))
  return `#${mix.map((v) => v.toString(16).padStart(2, '0')).join('')}`
}

/**
 * 将任务轨迹转为渲染数据：
 * - 按 recordedAt 升序、去除连续重复点（不足 2 个有效点返回 null）；
 * - 每个点带归一化时间 t 与由暗到亮的时间渐变色；
 * - 抽稀生成方向箭头（heading 为自北顺时针角度）。
 */
export function buildTrackRender(task: TrackTask): { points: TrackRenderPoint[]; arrows: TrackRenderArrow[] } | null {
  const sorted = task.track
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lon) && (p.lat !== 0 || p.lon !== 0))
    .sort((a, b) => a.recordedAt - b.recordedAt)

  const points: TrackPoint[] = []
  for (const p of sorted) {
    const last = points[points.length - 1]
    if (!last || last.lat !== p.lat || last.lon !== p.lon) points.push(p)
  }
  if (points.length < 2) return null

  const base = taskTrackColor(task.status)
  const dim = mixHex('#070b12', base, 0.45)
  const n = points.length
  const renderPoints: TrackRenderPoint[] = points.map((p, i) => {
    const t = i / (n - 1)
    return { lat: p.lat, lon: p.lon, t, color: mixHex(dim, base, t) }
  })

  const step = Math.max(1, Math.floor(n / 12))
  const arrows: TrackRenderArrow[] = []
  for (let i = step; i < n; i += step) {
    const a = points[i - 1]
    const b = points[i]
    const heading = ((Math.atan2(b.lon - a.lon, b.lat - a.lat) * 180) / Math.PI + 360) % 360
    arrows.push({ lat: b.lat, lon: b.lon, heading, color: base })
  }

  return { points: renderPoints, arrows }
}

export const EMPTY_TRACKS: TrackTask[] = []
